import { Col } from "react-bootstrap";
import Add from "../icon/Add";
import { formatPrice } from "../../constants";
import ProductArtworkPreview from "./ProductArtworkPreview";
import PackShowcaseCollage from "./PackShowcaseCollage";
import LazyShimmerImage from "../ui/LazyShimmerImage";
import {
    findPreferredProductColor,
    findPreferredProductSize,
    getAvailableStock,
    getProductColorLabel,
    getProductStockQuantity,
    isProductInStock,
    productNameIncludesSticker,
    resolveProductColorImage,
    resolveProductDisplayImage,
    uniqueProductColors,
    uniqueSortedProductSizes,
} from "../../services/productService";

function formatOptionCount(count: number, singular: string, plural: string): string {
    if (count <= 0) return "";
    return `${count} ${count === 1 ? singular : plural}`;
}

/** Build "3 colors · 9 sizes" — color count first / required when present. */
function formatColorSizeCountLine(colorCount: number, sizeCount: number): string {
    const colorPart = formatOptionCount(colorCount, "color", "colors");
    const sizePart = formatOptionCount(sizeCount, "size", "sizes");
    if (colorPart && sizePart) return `${colorPart} · ${sizePart}`;
    return colorPart || sizePart;
}

interface SingleMerchandiseCardProps {
    item: any;
    selected?: boolean;
    count?: number;
    onDecrease?: () => void;
    onIncrease?: () => void;
    onAddToCart?: () => void;
    onSelect?: () => void;
    favouriteImg?: any;
    variant?: "default" | "packs";
    packImageOverride?: string;
}

const SingleMerchandiseCard = ({
    item,
    selected,
    onSelect,
    onAddToCart,
    favouriteImg,
    variant = "default",
    packImageOverride,
}: SingleMerchandiseCardProps) => {
    const sku = item?.sku_code?.toLowerCase();
    const isPackCard = variant === "packs" && item?.pack === true;
    const packProducts = Array.isArray(item?.pack_products) && item.pack_products.length > 0
        ? item.pack_products
        : Array.isArray(item?.products)
            ? item.products
            : [];
    const packImage = item?.picture || item?.image || item?.imgURL || item?.image_url || packImageOverride;
    const packPrice = item?.pack_price ?? item?.price ?? 0;
    // Stickers use product_sizes (inches); hide color counts only.
    const isSticker = productNameIncludesSticker(item?.name);
    const cardSizes = uniqueSortedProductSizes(item?.product_sizes);
    const cardColors = isSticker
        ? []
        : uniqueProductColors([
              ...uniqueProductColors(item?.color),
              ...uniqueProductColors(
                  (Array.isArray(item?.product_sizes) ? item.product_sizes : [])
                      .map((row: any) => row?.color)
                      .filter((color: unknown) => color != null && color !== "")
              ),
          ]);
    // Prefer light default color (tote→beige, tee→white, hoodie→ash), then any in-stock color.
    const preferredColor = (() => {
        if (isSticker) return null;
        const preferred = findPreferredProductColor(cardColors, item);
        if (
            preferred &&
            isProductInStock(item, { colorLabel: getProductColorLabel(preferred) })
        ) {
            return preferred;
        }
        const inStockColor = cardColors.find((color) =>
            isProductInStock(item, { colorLabel: getProductColorLabel(color) })
        );
        return inStockColor || preferred;
    })();
    const colorLabel = preferredColor ? getProductColorLabel(preferredColor) : "";
    const preferredSize = findPreferredProductSize(item, preferredColor) || cardSizes[0] || null;
    const optionCountLine = formatColorSizeCountLine(
        cardColors.length,
        cardSizes.length
    );
    // Card "Sold out" only when every size/color variant is unavailable.
    const inStock = isProductInStock(item);
    const totalStock = getProductStockQuantity(item) ?? 0;
    const preferredVariantStock = getAvailableStock(item, {
        size: preferredSize,
        colorLabel: colorLabel || undefined,
    });
    const statusStock =
        preferredVariantStock > 0 ? preferredVariantStock : totalStock;
    // Quantity is chosen in the product modal — cards only open Add to cart.
    const canAddToCartStock = isSticker || inStock;

    const renderImageMapping = () => {
        const itemKey = item?.sku_code?.toLowerCase() || String(item?.id || "product");
        const productImage =
            (colorLabel ? resolveProductColorImage(item, colorLabel) : null) ||
            resolveProductDisplayImage(item);
        const preferredColorHasGeneratedMockup =
            preferredColor &&
            typeof preferredColor === "object" &&
            Boolean((preferredColor as { merchGeneratedMockup?: boolean }).merchGeneratedMockup);
        const useGeneratedMockup = Boolean(
            productImage &&
                (preferredColorHasGeneratedMockup ||
                    (item?.merchGeneratedMockup && cardColors.length === 0))
        );
        const productName =
            typeof item?.name === "string" ? item.name : item?.name?.name || "Product";
        const imageKey = useGeneratedMockup
            ? `img-${itemKey}-generated-${productImage}`
            : favouriteImg
                ? `img-${itemKey}-${favouriteImg}`
                : `img-${itemKey}`;

        if (useGeneratedMockup) {
            return (
                <LazyShimmerImage
                    key={imageKey}
                    src={productImage}
                    alt={productName}
                    className="packProductImage lazyShimmerImage--merch"
                />
            );
        }

        return (
            <ProductArtworkPreview
                key={imageKey}
                productImage={productImage}
                artworkImage={favouriteImg}
                itemKey={itemKey}
                productName={productName}
                skuCode={sku}
                placement={item?.artwork_placement || item?.artworkPlacement || item?.print_area}
                className="packProductImage"
                lazy
            />
        );
    };

    if (isPackCard) {
        return (
            <Col lg={6} xxl={6} sm={6} xs={6}>
                <div className={`singleFrameCard h-100 packProductCard packShowcaseCard ${selected ? "selected" : ""}`}>
                    <button type="button" className="packShowcaseButton" onClick={onSelect}>
                        <div className="packShowcaseTitle">
                            {typeof item?.name === "string" ? item.name : "Pack"}
                        </div>
                        <div className="packShowcaseImageWrap">
                            <PackShowcaseCollage
                                pack={item}
                                products={packProducts}
                                favouriteImg={favouriteImg}
                                fallbackImage={packImage}
                            />
                        </div>
                        <ul className="packShowcaseFeatures">
                            {packProducts.map((product: any, index: number) => {
                                const productName = product?.name || product?.product?.name || "Included product";
                                const quantity = Number(product?.quantity || 1);

                                return (
                                    <li key={product?.id || product?.product_id || `${productName}-${index}`}>
                                        <span className="packShowcaseCheck" aria-hidden="true">✓</span>
                                        <span>{quantity > 1 ? `${quantity} × ${productName}` : productName}</span>
                                    </li>
                                );
                            })}
                        </ul>
                        <div className="packShowcasePrice">{formatPrice(packPrice)}</div>
                    </button>
                </div>
            </Col>
        );
    }

    return (
        <Col lg={variant === "packs" ? 6 : 4} xxl={variant === "packs" ? 6 : 4} sm={6} xs={6}>
            <div className={`singleFrameCard h-100 ${selected ? "selected" : ""}${!inStock ? " soldOut" : ""}${variant === "packs" ? " packProductCard" : ""}${packImageOverride ? " has-pack-artwork" : variant === "packs" ? " packRetailCard" : ""}`}>
                <div className={`frameItem`}>
                    {packImageOverride ? (
                        <button type="button" className="packArtworkButton" onClick={onSelect}>
                            <LazyShimmerImage
                                src={packImageOverride}
                                className="packArtworkImage lazyShimmerImage--merch"
                                alt={typeof item?.name === "string" ? item.name : item?.name?.name || "Pack"}
                            />
                        </button>
                    ) : (
                        <div onClick={onSelect} role="presentation">
                            <div className="text-center packRetailImageSlot">
                                {renderImageMapping()}
                            </div>
                            <div className="name">
                                {typeof item?.name === "string" ? item.name : item?.name?.name || "Pack"}
                            </div>
                            <div className="price">{formatPrice(preferredSize?.price ?? item?.price)}</div>
                            <div className="d-flex align-items-center flex-wrap optionCountRow mb-0">
                                <div className="optionCountLine">{optionCountLine || "\u00a0"}</div>
                            </div>
                            <div
                                className={`stockStatusLine ${
                                    isSticker
                                        ? "isEmpty"
                                        : inStock
                                          ? "qtyOk"
                                          : "qtyDanger"
                                }`}
                            >
                                {isSticker
                                    ? "\u00a0"
                                    : inStock
                                      ? `In stock: ${statusStock} left`
                                      : "Sold out"}
                            </div>
                        </div>
                    )}
                    {!packImageOverride && (
                        <div
                            className={`counterPart counterPart--addOnly${
                                !isSticker && !inStock ? " soldOutActions" : ""
                            }`}
                            onClick={(e) => e.stopPropagation()}
                            role="presentation"
                        >
                            <button
                                type="button"
                                className={`unselectBtn${
                                    !isSticker && !inStock ? " soldOutBtn" : ""
                                }`}
                                disabled={!canAddToCartStock}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!canAddToCartStock) return;
                                    onAddToCart?.();
                                }}
                            >
                                {isSticker || inStock ? (
                                    <>
                                        Add to cart <span className="packAddIcon"><Add /></span>
                                    </>
                                ) : (
                                    "Sold Out"
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </Col>
    );
};

export default SingleMerchandiseCard;
